<script setup lang="ts">
import { computed } from 'vue'

type SearchInputSize = 'sm' | 'md' | 'lg'
type SearchInputVariant = 'default' | 'unified' | 'elevated'

const props = withDefaults(defineProps<{
  modelValue?: string
  placeholder?: string
  disabled?: boolean
  size?: SearchInputSize
  variant?: SearchInputVariant
  clearable?: boolean
  width?: string
}>(), {
  modelValue: '',
  placeholder: '搜索...',
  disabled: false,
  size: 'md',
  variant: 'elevated',
  clearable: true,
  width: '280px'
})

const emit = defineEmits<{
  'update:modelValue': [value: string]
  'search': [value: string]
  'clear': []
}>()

const wrapperStyle = computed(() => ({
  width: props.width
}))

// input 的固有宽度由 placeholder 决定：浏览器默认 size=20（≈20 个英文字符）会把
// width: max-content 的弹层撑到 ~234px，与内容无关。按 placeholder 字符数动态设 size
//（CJK 按 2 计，约等于其视觉宽度），弹层最小宽度便刚好能放下说明文本。
// 注意：size 只影响固有（intrinsic）宽度，实际布局仍由 CSS width: 100% 决定。
const placeholderSize = computed(() => {
  let units = 0
  for (const ch of props.placeholder) {
    units += (ch.codePointAt(0) ?? 0) > 0xff ? 2 : 1
  }
  return Math.max(units, 1)
})

function handleInput(event: Event): void {
  const target = event.target as HTMLInputElement
  emit('update:modelValue', target.value)
}

function handleClear(): void {
  emit('update:modelValue', '')
  emit('clear')
}

function handleKeydown(event: KeyboardEvent): void {
  if (event.key === 'Enter') {
    emit('search', props.modelValue)
  }
}
</script>

<template>
  <div class="search-input" :class="`search-input--${variant}`" :style="wrapperStyle">
    <svg class="search-input__icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
      <circle cx="11" cy="11" r="8"></circle>
      <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
    </svg>
    <input
      :class="['search-input__field', `search-input__field--${size}`]"
      :value="modelValue"
      :placeholder="placeholder"
      :size="placeholderSize"
      :disabled="disabled"
      @input="handleInput"
      @keydown="handleKeydown"
    />
    <button
      v-if="clearable && modelValue && !disabled"
      type="button"
      class="search-input__clear"
      @click="handleClear"
    >
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <line x1="18" y1="6" x2="6" y2="18"></line>
        <line x1="6" y1="6" x2="18" y2="18"></line>
      </svg>
    </button>
  </div>
</template>

<style lang="scss" scoped>
.search-input {
  position: relative;
  display: inline-flex;
  align-items: center;
}

.search-input__icon {
  position: absolute;
  left: 12px;
  color: var(--text-tertiary);
  pointer-events: none;
  flex-shrink: 0;
}

.search-input__field {
  width: 100%;
  color: var(--text-primary);
  font-size: var(--text-sm);
  outline: none;
  transition: all var(--transition-fast);
  padding-left: 38px;

  &--sm {
    padding-top: var(--spacing-1-5);
    padding-bottom: var(--spacing-1-5);
    padding-right: var(--spacing-2-5);
    font-size: var(--text-13);
  }

  &--md {
    padding-top: var(--spacing-2-5);
    padding-bottom: var(--spacing-2-5);
    padding-right: var(--spacing-3);
  }

  &--lg {
    padding-top: var(--spacing-3);
    padding-bottom: var(--spacing-3);
    padding-right: var(--spacing-3-5);
    font-size: var(--text-base);
  }

  &:hover:not(:disabled):not(:focus) {
    border-color: var(--border-hover);
  }

  &:focus {
    border-color: var(--primary);
    box-shadow: 0 0 0 3px var(--blue-alpha-15);
  }

  &::placeholder {
    color: var(--text-muted);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.search-input__clear {
  position: absolute;
  right: var(--spacing-2-5);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  background: transparent;
  border: none;
  color: var(--text-secondary);
  cursor: pointer;
  border-radius: var(--radius-sm);
  transition: all var(--transition-fast);

  &:hover {
    color: var(--text-primary);
    background: var(--surface-dim);
  }
}

// Variant: default
.search-input--default {
  .search-input__field {
    background: var(--bg-tertiary);
    border: 1px solid var(--border-color);
    border-radius: var(--radius);
    box-shadow: var(--shadow-input);
  }
}

// Variant: unified
.search-input--unified {
  // 240px 是设计下限；用 min() 让下限不突破容器宽度（注意：CSS 中 min-width 优先于
  // max-width，max-width 兜底无效），窄容器（如下拉弹层）内自动收窄，使用方无需局部覆盖
  min-width: min(240px, 100%);

  .search-input__icon {
    left: 12px;
    color: var(--text-tertiary);
  }

  .search-input__field {
    background: var(--surface-bright);
    border: var(--input-border-width) solid var(--border-color);
    border-radius: var(--input-radius);
    color: var(--text-primary);
    box-shadow: var(--shadow-input);

    &--sm {
      height: 36px;
      padding-top: 0;
      padding-bottom: 0;
    }

    &--md {
      height: 40px;
      padding-top: 0;
      padding-bottom: 0;
    }

    &:hover:not(:disabled):not(:focus) {
      border-color: var(--border-hover);
      background: var(--surface-bright);
    }

    &:focus {
      border-color: var(--primary-300);
      box-shadow: var(--shadow-input), 0 0 0 2px var(--primary-100);
      background: var(--surface-bright);
    }
  }

  .search-input__clear {
    color: var(--text-secondary);
  }
}

// Variant: elevated (立体效果，参考 BuildingCardGrid 立体搜索框)
.search-input--elevated {
  .search-input__field {
    background: var(--surface-bright);
    border: 1px solid var(--border-color);
    border-radius: var(--radius-xl);
    color: var(--text-primary);
    box-shadow: var(--shadow-input);

    &--sm {
      height: 36px;
      padding-top: 0;
      padding-bottom: 0;
    }

    &--md {
      height: 40px;
      padding-top: 0;
      padding-bottom: 0;
    }

    &--lg {
      height: 50px;
      padding-top: 0;
      padding-bottom: 0;
    }

    &:hover:not(:disabled):not(:focus) {
      border-color: var(--primary-200);
    }

    &:focus {
      border-color: var(--primary);
      box-shadow: var(--shadow-input), 0 0 0 3px var(--primary-alpha-15);
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }
}
</style>
